/**
 * TradeStack - Jobs List Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Jobs.css';

function Jobs() {
  const { config, formatCurrency, formatDate } = useConfig();
  const [searchParams, setSearchParams] = useSearchParams();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ total: 0 });

  const statusFilter = searchParams.get('status') || '';
  const searchQuery = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page')) || 1;

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchQuery, page]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await api.getJobs({
        status: statusFilter,
        search: searchQuery,
        limit: 20,
        offset: (page - 1) * 20
      });
      console.log('Jobs API response:', response.data);
      setJobs(response.data.jobs || []);
      setPagination({ total: response.data.total || 0 });
    } catch (err) {
      setError('Failed to load jobs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusFilter = (status) => {
    const params = new URLSearchParams(searchParams);
    if (status) {
      params.set('status', status);
    } else {
      params.delete('status');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const search = formData.get('search');
    const params = new URLSearchParams(searchParams);
    if (search) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const getStatusBadge = (status) => {
    const statusConfig = config?.jobStatuses?.find(s => s.id === status);
    return (
      <span className={`status-badge status-${status}`}>
        {statusConfig?.name || statusConfig?.label || status}
      </span>
    );
  };

  const statuses = config?.jobStatuses || [
    { id: 'lead', name: 'Lead' },
    { id: 'quoted', name: 'Quoted' },
    { id: 'scheduled', name: 'Scheduled' },
    { id: 'in_progress', name: 'In Progress' },
    { id: 'completed', name: 'Completed' }
  ];

  return (
    <div className="jobs-page">
      <div className="page-header">
        <h1>Jobs</h1>
        <Link to="/jobs/new" className="btn btn-primary">+ New Job</Link>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="status-filters">
          <button
            className={`filter-btn ${!statusFilter ? 'active' : ''}`}
            onClick={() => handleStatusFilter('')}
          >
            All
          </button>
          {statuses.map(status => (
            <button
              key={status.id}
              className={`filter-btn ${statusFilter === status.id ? 'active' : ''}`}
              onClick={() => handleStatusFilter(status.id)}
            >
              {status.name || status.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            name="search"
            placeholder="Search jobs..."
            defaultValue={searchQuery}
          />
          <button type="submit" className="btn btn-secondary">Search</button>
        </form>
      </div>

      {/* Jobs List */}
      {loading ? (
        <div className="loading-state">Loading jobs...</div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No jobs found</h3>
          <p>Create your first job to get started</p>
          <Link to="/jobs/new" className="btn btn-primary">Create Job</Link>
        </div>
      ) : (
        <div className="jobs-list">
          {jobs.map(job => (
            <Link to={`/jobs/${job.id}`} key={job.id} className="job-card">
              <div className="job-header">
                <h3 className="job-title">{job.title}</h3>
                {getStatusBadge(job.status)}
              </div>
              <div className="job-customer">
                {job.customer_company || (job.customer_first_name ? `${job.customer_first_name} ${job.customer_last_name}` : 'No customer assigned')}
              </div>
              <div className="job-details">
                {job.address_line1 && (
                  <span className="job-address">{job.address_line1}</span>
                )}
                {job.scheduled_start && (
                  <span className="job-date">
                    Scheduled: {formatDate(job.scheduled_start)}
                  </span>
                )}
              </div>
              <div className="job-footer">
                <span className="job-value">
                  {formatCurrency(job.estimated_value || 0)}
                </span>
                {job.progress_percent !== undefined && (
                  <div className="job-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${job.progress_percent}%` }}
                      />
                    </div>
                    <span className="progress-text">{job.progress_percent}%</span>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total > 20 && (
        <div className="pagination">
          <button
            className="btn btn-secondary"
            disabled={page === 1}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('page', page - 1);
              setSearchParams(params);
            }}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {page} of {Math.ceil(pagination.total / 20)}
          </span>
          <button
            className="btn btn-secondary"
            disabled={page >= Math.ceil(pagination.total / 20)}
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set('page', page + 1);
              setSearchParams(params);
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Jobs;
